import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeAll, mergeMap, reduce, shareReplay, take, timeout } from 'rxjs/operators';

import { AppSettings } from '../configuration/appsettings';
import { Database } from '../configuration/idatabase.interface';
import { IServer } from '../configuration/iserver.interface';

interface DbSelectionList {
	id: string;
	serverId: number;
	serverName: string;
	address: string;
	apyType: number;
	databaseId: number;
	databaseName: string;
	desc?: string;
}

/**
 * Authentication service
 */
@Injectable({
	providedIn: 'root',
	deps: [HttpClient, AppSettings],
})
export class ServerProviderService {
	private showServerSettings = this.app.settings.showServerSettings;

	/** onservable containing the list of servers */
	public serverList$ = this.showServerSettings
		? this.http
				.get<IServer[]>(`${this.app.settings.serverProviderAddress}${this.app.settings.customerId}`)
				.pipe(shareReplay(1), take<IServer[]>(1))
		: of([]);

	/** Observable containng the servers including their databses */
	public databaseServers$: Observable<IServer[]> = this.showServerSettings
		? this.serverList$.pipe(
				mergeMap((servers: IServer[]) => servers.map(this.fetchDbList)),
				mergeAll(),
				reduce((sList: IServer[], i: IServer) => {
					return sList.concat(i);
				}, []),
				catchError(r => {
					console.error(r); //NOSONAR
					return throwError(r);
				}),
				shareReplay(1), // don't refetch during lifetime
				take<IServer[]>(1) // do complete.
		  )
		: of([]);

	public databaseList$ = this.showServerSettings
		? this.databaseServers$.pipe(
				map(r => r.filter(i => i.databases.length > 0)),
				map<IServer[], DbSelectionList[]>(serverList =>
					serverList.reduce((list, server) => {
						server.databases.forEach(db => {
							list.push({
								id: `${server.id}-${db.id}`,
								serverId: server.id,
								serverName: server.name,
								address: server.address,
								apyType: server.apiType,
								databaseId: db.id,
								databaseName: db.name,
								desc: db.description,
							});
						});
						return list;
					}, [])
				)
		  )
		: of([]);

	/**
	 *
	 */
	constructor(private http: HttpClient, private app: AppSettings) {
		this.fetchDbList = this.fetchDbList.bind(this);
	}

	/**
	 * Fetch a list of available databases for the given server
	 */
	private fetchDbList(server: IServer): Observable<IServer> {
		const url = server.address + this.app.settings.serverSettings[server.apiType].dbListUrl;
		return this.http.get<Database>(url).pipe(
			timeout(7500), // timeout in 7.5 seconds, long enough??
			catchError(() => of([])),
			map(
				databases =>
					({
						...server,
						databases,
					} as IServer)
			)
		);
	}
}
